/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { FancyActionableFirebase } from '../../common/fancy-firebase/generics/FancyActionableFirebase';
import { dateCache, DefaultSplitDataRenderType, FancyAction, Plan } from '@touchplan/common';
import { IPromisePeriod, PromisePeriod } from '../models/PromisePeriod';
import { PromisePeriodList } from '../../common/fancy-firebase/lists/PromisePeriodList';
import { PlansApi } from './PlansApi';
import { ActiveLineObject } from '../../common/fancy-firebase/objects/ActiveLineObject';
import { PlanAccessCheck } from '../../services/PlanAccessCheck';
import {
	ACTIVE_LINE_PIN_RIGHT,
	PIN_HISTORY_DUPLICATE_DATES,
	UNABLE_TO_PERFORM_ACTION_BECAUSE_NOT_ADMIN,
	ACTION_SET_NEW_PROMISE_PERIOD,
	PROMISE_PERIOD_ID_NOT_FOUND,
	PROMISE_PERIOD_ALREADY_LOCKED,
	PROMISE_PERIOD_NO_ACCESS,
	PROMISE_PERIOD_ERROR_ON_SNAPSHOT,
} from '@touchplan/strings';
import { FirebaseSnapshotsService } from '../../services/firebase-snapshots.service';
import { ProjectObject } from '../../common/fancy-firebase/objects/ProjectObject';
/**
 * PromisePeriodApi
 * Handles all of the Firebase "write" operations as well as local state changes.
 * You should not be using this class directly and instead should be going through PromisePeriodService.
 */
export class PromisePeriodApi extends FancyActionableFirebase<PromisePeriod, FancyAction, PromisePeriodList> {
	constructor(
		public data: PromisePeriodList,
		private plansApi: PlansApi,
		private activeLineObject: ActiveLineObject,
		private planAccess: PlanAccessCheck,
		private firebaseSnapshot: FirebaseSnapshotsService,
		private project: ProjectObject
	) {
		super(data);
	}
	/**
	 * Writes {locked:true} for the PromisePeriod matching ID.
	 * This will lock the provided promise period and then push a new period to the stack
	 * @param id The ID of the Promise Period to modify
	 * @param plan The Plan to add the new promise period to
	 */
	public lockPeriod(id: string, plan: Plan): void {
		const promisePeriod: PromisePeriod = this.data.syncList.get(id);
		if (!promisePeriod) {
			Logging.warning(PROMISE_PERIOD_ID_NOT_FOUND);
			return;
		}

		if (promisePeriod.view.locked) {
			Logging.warning(PROMISE_PERIOD_ALREADY_LOCKED);
			return;
		}

		if (!this.canLockPeriod(promisePeriod.view.endDate)) {
			Logging.warning(ACTIVE_LINE_PIN_RIGHT);
			return;
		}

		let extra = { initiatedBy: 'systemOnPromisePeriodLock' };
		this.firebaseSnapshot.takeSnapshot$(this.project.syncObject.projectId, plan.$id, extra).subscribe({
			next: () => {
				this.localUpdate(id, { locked: true });
				this.createFromPreviousPeriod(promisePeriod);
				this.plansApi.setPromisePeriodPosition(plan, promisePeriod.$id);
				this.persistData(promisePeriod, DefaultSplitDataRenderType.database);
			},
			error: (error) => {
				Logging.error(PROMISE_PERIOD_ERROR_ON_SNAPSHOT);
			},
		});
	}
	/**
	 * Writes {minimized:true} for the PromisePeriod matching ID
	 * @param id The ID of the Promise Period to modify
	 */
	public minimize(period: PromisePeriod): void {
		if (!this.canModifyPromisePeriod()) {
			Logging.warning(PROMISE_PERIOD_NO_ACCESS);
			return;
		}

		this.localUpdate(period.$id, { minimized: true });
		this.persistData(period, DefaultSplitDataRenderType.database);
	}
	/**
	 * Writes {minimized:false} for the PromisePeriod matching ID
	 * @param id The ID of the Promise Period to modify
	 */
	public expand(period: PromisePeriod): void {
		if (!this.canModifyPromisePeriod()) {
			Logging.warning(PROMISE_PERIOD_NO_ACCESS);
			return;
		}

		this.localUpdate(period.$id, { minimized: false });
		this.persistData(period, DefaultSplitDataRenderType.database);
	}
	/**
	 * Toggle the minimized flag for the promise period matching ID
	 * @param id The ID of the Promise Period to modify
	 */
	public toggleMinimize(id: string): void {
		if (!this.canModifyPromisePeriod()) {
			Logging.warning(PROMISE_PERIOD_NO_ACCESS);
			return;
		}

		const promisePeriod: PromisePeriod = this.data.syncList.get(id);
		if (!promisePeriod) {
			Logging.warning(PROMISE_PERIOD_ID_NOT_FOUND);
			return;
		}

		this.localUpdate(id, { minimized: !promisePeriod.view.minimized });
		this.persistData(promisePeriod, DefaultSplitDataRenderType.database);
	}

	/**
	 * Pushes a new PromisePeriod object to the promisePeriodStack in Firebase
	 * @param startDate The start date of the next promise period
	 * @param endDate The end date of the next promise period
	 * @param shouldStartMinimized Override to set if the new promise period should start minimized or not
	 */
	public create(startDate: string, endDate: string, shouldStartMinimized: boolean = false): void {
		if (!this.canModifyPromisePeriod()) {
			Logging.warning(UNABLE_TO_PERFORM_ACTION_BECAUSE_NOT_ADMIN(ACTION_SET_NEW_PROMISE_PERIOD));
			return;
		}

		const key: string = this.data.generateNewChildKey();
		const cappedEndDate: string = this.getCappedEndDate(endDate);
		const promisePeriod: IPromisePeriod = {
			startDate,
			endDate: cappedEndDate,
			locked: false,
			minimized: shouldStartMinimized,
		};

		this.addItem(promisePeriod, key);
		const item: PromisePeriod = this.data.syncList.get(key);
		this.persistData(item, DefaultSplitDataRenderType.database);
	}
	/**
	 * Helper method to push a new promise period item to the stack using calculated values from a previous promise period
	 * @param period The previous period to create new data from
	 * @param plan The plan to add this promise period to
	 */
	public createFromPreviousPeriod(period: PromisePeriod): void {
		// Calculate the size of the previous period, this is to ensure the next period is the same size as the period
		// that was just locked. Then add 1 day to the start
		const diff: number = dateCache.diffDays(period.view.endDate, period.view.startDate);
		// New start date should be the previous end date plus one day.
		const newStartDate: string = dateCache.addDays(period.view.endDate, 1);
		// New end date should be the new start date plus the total days of the previous locked period
		const newEndDate: string = dateCache.addDays(newStartDate, diff);
		this.create(newStartDate, newEndDate);
	}

	/**
	 * Helper function to check if we can lock the current period
	 * This ensures the user has the correct permissions and that the next period will not go into pull space.
	 * @param lastEndDate
	 * @private
	 */
	private canLockPeriod(lastEndDate: string): boolean {
		if (!this.canModifyPromisePeriod()) {
			return false;
		}

		const nextStartDate: string = dateCache.addDays(lastEndDate, 1);
		const activeLineDate: string = this.activeLineObject.syncObject.view?.activeLineDate || nextStartDate;

		if (nextStartDate <= lastEndDate) {
			Logging.warning(PIN_HISTORY_DUPLICATE_DATES);
		}
		return !dateCache.isSameOrAfter(new Date(nextStartDate), activeLineDate);
	}

	/**
	 * Helper method to check if the current user is allowed to modify the promise periods
	 * @private
	 */
	private canModifyPromisePeriod(): boolean {
		if (!this.planAccess.isAdminSync) {
			return false;
		}
		// `hasRestrictedAccessSync` doesn't check if a user "has restricted access", like the name implies
		// but rather is a "is this plan editable"? check. So the value should be false is the plan can be edited
		// and true if the plan is in some "read only" state and should not be modified.
		if (this.planAccess.hasRestrictedAccessSync) {
			return false;
		}

		return true;
	}

	/**
	 * Helper function to get a clamped date (should not go past the Active Line)
	 * @param endDate
	 * @private
	 */
	private getCappedEndDate(endDate: string): string {
		const activeLineDate: string = this.activeLineObject.syncObject.view?.activeLineDate || endDate;
		return dateCache.isBefore(new Date(endDate), activeLineDate) ? endDate : activeLineDate;
	}
}
